import { z } from "zod/v3";
import { PlainMessage } from "@bufbuild/protobuf";
import { ZodOutputFor, createSchema } from "../zod-utils";
import { ShuntingUnit as ProtoShuntingUnit } from "@opstel/gen/knop/ui/v1/model/shunting_unit_pb";
import { PlanTrainUnit, TrainUnitKind, planTrainUnitSchema } from "./planTrainUnit";
import { TrainUnitList } from "./types/trainUnitList";
import { Length } from "@shared/common/utils/length";

export interface ShuntingUnit {
  guid: string;
  name: string;
  planTrainUnits: TrainUnitList<PlanTrainUnit>;
}
interface IntermediateShuntingUnit {
  guid: string;
  name: string;
  planTrainUnits: PlanTrainUnit[];
}

export const shuntingUnitSchema = createSchema<
  IntermediateShuntingUnit,
  PlainMessage<ProtoShuntingUnit>
>(
  {
    guid: z.string().uuid({ message: "ShuntingUnit guid should be a valid uuid" }),
    name: z.string().min(1, { message: "ShuntingUnit name should not be empty" }),
    planTrainUnits: z.array(planTrainUnitSchema),
  },
  { message: "ShuntingUnit should be set" },
).transform(({ planTrainUnits, ...rest }) => ({
  ...rest,
  planTrainUnits: new TrainUnitList(planTrainUnits),
})) satisfies ZodOutputFor<ShuntingUnit>;

export const ShuntingUnit = {
  getTrainTypes: (shuntingUnit: ShuntingUnit): string[] =>
    shuntingUnit.planTrainUnits.fromSourceToTarget("FirstUnitOnSourceSide").map((ptu) => ptu.type),
  totalLength: (shuntingUnit: ShuntingUnit): Length =>
    new Length(
      shuntingUnit.planTrainUnits
        .inAnyDirection()
        .map((ptu) => ptu.length.inMillimeters())
        .reduce((a, b) => a + b, 0),
    ),
  getTrainUnitKind: (shuntingUnit: ShuntingUnit): TrainUnitKind =>
    shuntingUnit.planTrainUnits.inAnyDirection()[0].kind,
};
